import React, { useMemo, useCallback } from "react";
import { useToggle } from "react-use";

import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { Route } from "lib/routes";
import { useAppRouter } from "lib/useAppRouter";

import { PayrollSortOrder } from "graphql/types";

import {
  CompanyBenefitFragment,
  PagedPayrollItemFragment,
  PayrollItemFragment,
  PayrollProviderFragment,
  usePayrollQuery,
  usePayrollsQuery,
} from "./graphql.generated";

export const calculateTotal = (payroll: PagedPayrollItemFragment) => {
  return (
    Number(payroll.benefits) +
    Number(payroll.employeeBenefitsDeductions) +
    Number(payroll.employeeTaxes) +
    Number(payroll.employerTaxes) +
    Number(payroll.netPay)
  );
};

const PayrollPageContext = React.createContext<{
  pagedPayrolls: PagedPayrollItemFragment[];
  activePayroll?: PayrollItemFragment;
  pagedPayrollsLoading: boolean;
  activePayrollLoading: boolean;
  hasMore?: boolean;
  companyId: string;
  totalSum: number;
  fetchNextPage: () => void;
  setActivePayroll: (id?: string) => void;
  popoverOpen: boolean;
  setPopoverOpen: (open: boolean) => void;
  slug?: string;
  activePayrollIndex: number;
  companyLevelBenefits: CompanyBenefitFragment[];
  payrollProviders: PayrollProviderFragment[];
} | null>(null);

export const PayrollProvider = ({
  children,
  slug,
}: {
  children?: React.ReactNode;
  slug?: string;
}) => {
  const { company } = useActiveCompany<true>();
  const { goToPath } = useAppRouter();

  const setActivePayroll = useCallback(
    (id?: string) => {
      if (!id) {
        goToPath(Route.payroll);
        return;
      }

      goToPath(`${Route.payroll}/${id}`);
    },
    [goToPath]
  );

  const [popoverOpen, setPopoverOpen] = useToggle(false);

  const {
    data: pagedPayrolls,
    loading: pagedPayrollsLoading,
    variables,
    fetchMore,
  } = usePayrollsQuery({
    context: { batch: !process.env.MOCK_PAYROLL },
    notifyOnNetworkStatusChange: true,
    variables: {
      companyId: company.id,
      sortBy: PayrollSortOrder.CheckDateOrderDesc,
      page: {
        count: 13,
        after: null,
      },
    },
  });

  const getDefaultPayrollId = useCallback(() => {
    const firstPayroll = pagedPayrolls?.company?.payrolls?.nodes[0];

    if (slug) return slug;
    if (firstPayroll) return firstPayroll.id;

    return null;
  }, [slug, pagedPayrolls]);

  const defaultPayrollId = getDefaultPayrollId();
  const {
    data: activePayroll,
    loading: activePayrollLoading,
    refetch,
  } = usePayrollQuery({
    skip: !defaultPayrollId,
    variables: {
      id: defaultPayrollId ?? "",
    },
  });

  const fetchNextPage = useCallback(() => {
    if (!variables) {
      return;
    }

    fetchMore({
      variables: {
        ...variables,
        page: {
          ...variables.page,
          after: pagedPayrolls?.company?.payrolls?.pageInfo.endCursor,
        },
      },
    });
  }, [pagedPayrolls, fetchMore, variables]);

  const payrolls = useMemo(() => pagedPayrolls?.company?.payrolls?.nodes ?? [], [pagedPayrolls]);
  const companyLevelBenefits = useMemo(
    () => pagedPayrolls?.company?.companyBenefits ?? [],
    [pagedPayrolls]
  );
  const payrollProviders = useMemo(
    () => pagedPayrolls?.company?.payrollProviders ?? [],
    [pagedPayrolls]
  );
  const totalSum = useMemo(
    () => payrolls.reduce((acc, val) => acc + calculateTotal(val), 0),
    [payrolls]
  );
  const activePayrollIndex = useMemo(() => {
    return slug ? payrolls.findIndex(({ id }) => id === slug) : 0;
  }, [slug, payrolls]);

  const context = useMemo(
    () => ({
      pagedPayrolls: payrolls,
      activePayroll: activePayroll?.payroll,
      activePayrollLoading,
      pagedPayrollsLoading: pagedPayrollsLoading,
      companyLevelBenefits,
      payrollProviders,
      companyId: company.id,
      hasMore: pagedPayrolls?.company?.payrolls?.pageInfo.hasNextPage,
      totalSum,
      fetchNextPage,
      setActivePayroll,
      activePayrollIndex,
      popoverOpen,
      setPopoverOpen,
      slug,
      refetch,
    }),
    [
      pagedPayrollsLoading,
      activePayrollLoading,
      activePayroll,
      company,
      pagedPayrolls,
      totalSum,
      fetchNextPage,
      setActivePayroll,
      activePayrollIndex,
      popoverOpen,
      setPopoverOpen,
      payrolls,
      slug,
      refetch,
      companyLevelBenefits,
      payrollProviders,
    ]
  );

  return <PayrollPageContext.Provider value={context}>{children}</PayrollPageContext.Provider>;
};

export const usePayrollPage = () => {
  const context = React.useContext(PayrollPageContext);
  if (context === null) {
    throw new Error("PayrollPageContext not found");
  }
  return context;
};
